<template>
  <v-container fluid class="bilanca">

    <div id="printArea">

      <!-- zaglavlje izvjestaja -->
      <div class="bl-head">
        <div class="bl-firma">
          <div class="bl-firma-ime">{{ comp.CompanyNameShort }}</div>
          <div>{{ comp.CompanyStreet }} {{ comp.CompanyBuildingNameNumber }}</div>
          <div>{{ comp.CompanyPostcode }} {{ comp.CompanyCity }}</div>
          <div>OIB: {{ comp.CompanyVATID }}</div>
        </div>

        <div class="bl-naslov">
          <h2 v-if="$store.state.language === 'EN'">Balance sheet</h2>
          <h2 v-else>Bruto bilanca</h2>
          <div class="bl-meta">
            {{ currency }}
            <span class="bl-sep">·</span>
            {{ head.docFromDate ? localDate(head.docFromDate) : '' }} – {{ head.docToDate ? localDate(head.docToDate) : '' }}
            <span class="bl-sep">·</span>
            konta {{ head.fromAccount }}–{{ head.toAccount }}
          </div>
        </div>

        <div class="bl-akcije">
          <v-btn small outlined color="primary" @click="print">Printaj</v-btn>
          <v-btn small outlined @click="downloadFile">Excel</v-btn>
          <v-btn small outlined @click="rdg(1)">RDG</v-btn>
          <v-btn small outlined @click="rdg(2)">GFI-POD</v-btn>
        </div>
      </div>

      <!-- kontrole -->
      <div class="bl-kontrole">
        <span class="bl-label">Razina</span>
        <v-btn-toggle v-model="razina" mandatory dense class="bl-toggle">
          <v-btn small :value="1">Klasa</v-btn>
          <v-btn small :value="2">Sintetika</v-btn>
          <v-btn small :value="3">Sin.3</v-btn>
          <v-btn small :value="4">Sva konta</v-btn>
        </v-btn-toggle>

        <span class="bl-label bl-label2">Kolone</span>
        <v-btn-toggle v-model="prikaz" mandatory dense class="bl-toggle">
          <v-btn small value="sazeto">Sažeto</v-btn>
          <v-btn small value="detaljno">Detaljno</v-btn>
        </v-btn-toggle>

        <span class="bl-broj">
          {{ vidljivi.length }} redaka prikazano · {{ brojKonta }} konta u bilanci
          <span v-if="prikaz === 'detaljno'" class="bl-savjet">· klik na konto otvara karticu</span>
        </span>
      </div>

      <!-- tablica -->
      <div class="bl-scroll">
        <table class="bl-tbl">

          <colgroup v-if="prikaz === 'sazeto'">
            <col style="width:74px"><col><col style="width:134px">
            <col style="width:134px"><col style="width:152px">
          </colgroup>
          <colgroup v-else>
            <col style="width:74px"><col>
            <col style="width:116px"><col style="width:116px">
            <col style="width:116px"><col style="width:116px">
            <col style="width:116px"><col style="width:116px"><col style="width:134px">
          </colgroup>

          <thead v-if="prikaz === 'sazeto'">
            <tr class="tier2">
              <th class="l">Konto</th>
              <th class="l">Naziv konta</th>
              <th>Duguje</th>
              <th>Potražuje</th>
              <th>Saldo</th>
            </tr>
          </thead>

          <thead v-else>
            <tr class="tier1">
              <th class="l"></th>
              <th class="l"></th>
              <th class="span gstart" colspan="2"><span>Početno stanje</span></th>
              <th class="span gstart" colspan="2"><span>Promet u razdoblju</span></th>
              <th class="span gstart g-tot" colspan="3"><span>Ukupno</span></th>
            </tr>
            <tr class="tier2">
              <th class="l">Konto</th>
              <th class="l">Naziv konta</th>
              <th class="gstart">Duguje</th>
              <th>Potražuje</th>
              <th class="gstart">Duguje</th>
              <th>Potražuje</th>
              <th class="gstart">Duguje</th>
              <th>Potražuje</th>
              <th>Saldo</th>
            </tr>
          </thead>

          <tbody>
            <tr
              v-for="r in vidljivi"
              :key="r.key"
              :class="['lvl' + r.level, klikabilan(r) ? 'klik' : '']"
              :tabindex="klikabilan(r) ? 0 : null"
              @click="klikRed(r)"
              @keydown.enter="klikRed(r)"
            >
              <td class="konto">{{ r.code }}</td>
              <td class="naziv">
                <span class="uvlaka" :style="{width: ((r.level - 1) * 14) + 'px'}"></span>
                <button
                  v-if="r.kids.length"
                  type="button"
                  class="strelica"
                  :class="{otvoren: r.open}"
                  :aria-expanded="String(r.open)"
                  @click.stop="prebaci(r)"
                >▶</button>
                <span v-else class="strelica prazna">▶</span>
                <span class="naziv-tekst">{{ r.name }}</span>
                <span v-if="klikabilan(r)" class="kartica-hint">kartica ›</span>
              </td>

              <template v-if="prikaz === 'sazeto'">
                <td class="num" :class="{nula: r.fTotD === '–'}">{{ r.fTotD }}</td>
                <td class="num" :class="{nula: r.fTotP === '–'}">{{ r.fTotP }}</td>
                <td class="num" :class="{nula: r.fSaldo === '–', neg: r.negativan}">
                  {{ r.fSaldo }}<span class="dp">{{ r.dp }}</span>
                </td>
              </template>

              <template v-else>
                <td class="num gstart" :class="{nula: r.fPsD === '–'}">{{ r.fPsD }}</td>
                <td class="num" :class="{nula: r.fPsP === '–'}">{{ r.fPsP }}</td>
                <td class="num gstart" :class="{nula: r.fPrD === '–'}">{{ r.fPrD }}</td>
                <td class="num" :class="{nula: r.fPrP === '–'}">{{ r.fPrP }}</td>
                <td class="num gstart" :class="{nula: r.fTotD === '–'}">{{ r.fTotD }}</td>
                <td class="num" :class="{nula: r.fTotP === '–'}">{{ r.fTotP }}</td>
                <td class="num" :class="{nula: r.fSaldo === '–', neg: r.negativan}">
                  {{ r.fSaldo }}<span class="dp">{{ r.dp }}</span>
                </td>
              </template>
            </tr>
          </tbody>

          <tfoot>
            <tr class="uk">
              <td class="konto"></td>
              <td class="naziv">UKUPNO {{ currency }}</td>
              <template v-if="prikaz === 'sazeto'">
                <td class="num">{{ formatPrice(zbroj.totD) }}</td>
                <td class="num">{{ formatPrice(zbroj.totP) }}</td>
                <td class="num">{{ formatPrice(zbroj.saldo) }}</td>
              </template>
              <template v-else>
                <td class="num gstart">{{ formatPrice(zbroj.psD) }}</td>
                <td class="num">{{ formatPrice(zbroj.psP) }}</td>
                <td class="num gstart">{{ formatPrice(zbroj.prD) }}</td>
                <td class="num">{{ formatPrice(zbroj.prP) }}</td>
                <td class="num gstart">{{ formatPrice(zbroj.totD) }}</td>
                <td class="num">{{ formatPrice(zbroj.totP) }}</td>
                <td class="num">{{ formatPrice(zbroj.saldo) }}</td>
              </template>
            </tr>
          </tfoot>

        </table>
      </div>

      <!-- kontrolna linija -->
      <div class="bl-foot">
        <span class="bl-check" :class="uravnotezena ? 'ok' : 'lose'">
          {{ uravnotezena ? '✓ Duguje = Potražuje' : '✕ Bilanca nije u ravnoteži' }}
        </span>
        <span class="bl-sums">
          <span><em>Ukupno duguje</em> {{ formatPrice(zbroj.totD) }}</span>
          <span><em>Ukupno potražuje</em> {{ formatPrice(zbroj.totP) }}</span>
          <span><em>Saldo</em> {{ formatPrice(zbroj.saldo) }}</span>
        </span>
      </div>

    </div>

    <MainLedgerAccountCard ref="kartica" />
    <MainLedgerStatement ref="izvjestaj" />

  </v-container>
</template>

<script>
import Account1Service from '@/services/Account1Service'
import Account2Service from '@/services/Account2Service'
import Account3Service from '@/services/Account3Service'
import MainLedgerAccountCard from './MainLedgerAccountCard'
import MainLedgerStatement from './MainLedgerStatement'
import {printBalancePdf} from './mainLedgerPdf'
import {exportBalanceXlsx} from './mainLedgerXlsx'
import MainLedgerDetailService from '@/services/MainLedgerDetailService'
import ledgerEN from './MainLedgerDescEN'
import ledgerHR from './MainLedgerDescHR'
import commEn from "@/language/commonEN"
import commHr from "@/language/commonHR"
import dateFormat from '@/snippets/dateFormat'
import { round, parseNum} from '@/snippets/allSnippets'
import doAll from './gfipod'
import { domacaValuta } from '@/snippets/domacaValuta'

export default {
  name: 'mainledgercard1',
  props: ['items', 'psItems', 'account4Loc', 'head', 'showEUR'],
  data () {
    return {
      menu: false,
      menu2: false,
      menu3: false,
      menu4: false,
      dateFormatted: null,
      dateFormatted2: null,
      modal: false,
      modal1: false,
      modal2: false,
      modal3: false,
      modal4: false,
      dialog1: false,
      dialog2: false,
      dialog3: false,
      dialog4: false,
      date1: null,
      date2: null,
      msg: '',
      valid: true,
      comp: {},
      error: null,
      localError: false,
      newUser: {},
      size: 'sm',
      havePath: '',
      Total1: 0,
      Total2: 0,
      psTotal1: 0,
      psTotal2: 0,
      yearTotal1: 0,
      yearTotal2: 0,
      Total3: 0,
      Total4: 0,
      Total5: 0,
      oneTotal1: 0,
      oneTotal2: 0,
      onePsTotal1: 0,
      onePsTotal2: 0,
      oneYearTotal1: 0,
      oneYearTotal2: 0,
      oneTotal3: 0,
      oneTotal4: 0,
      oneTotal5: 0,
      twoTotal1: 0,
      twoTotal2: 0,
      twoPsTotal1: 0,
      twoPsTotal2: 0,
      twoYearTotal1: 0,
      twoYearTotal2: 0,
      twoTotal3: 0,
      twoTotal4: 0,
      twoTotal5: 0,
      threeTotal1: 0,
      threeTotal2: 0,
      threePsTotal1: 0,
      threePsTotal2: 0,
      threeYearTotal1: 0,
      threeYearTotal2: 0,
      threeTotal3: 0,
      threeTotal4: 0,
      threeTotal5: 0,
      oneCond: null,
      twoCond: null,
      threeCond: null,
      PrintDate: null,
      lang: {},
      langC: {},
      saving: false,
      pItems: [],
      account1s: [],
      account2s: [],
      account3s: [],
      lastOne: {},
      asLast: {},
      freeLine: ' ',
      balance: {},
      exRate: 1,
      currency: 'EUR',
      allId: 0,
      fullGFI: [],
      nI: {},
      nA: {},
      nB: {},
      nC: {},
      fRDG:{},
      lItems: [],
      lPsItems: [],
      pOldItems: [],
      pYitems: [],
      useYear: 1,
      fullAdmin: false,
      // prikaz bilance
      razina: 2,
      prikaz: 'sazeto',
      stablo: [],
      brojKonta: 0
    }
  },
  async mounted () {
    if (this.$store.state.language === "EN") {
      this.lang = ledgerEN
      this.langC = commEn;
    }
    if (this.$store.state.language === "HR") {
      this.lang = ledgerHR
      this.langC = commHr;
    }
    if (this.$store.state.user.Admin) {
      this.fullAdmin = this.$store.state.user.Admin
    }
    
    this.account1s = (await Account1Service.show()).data.account1s
    this.account2s = (await Account2Service.show()).data.account2s
    this.account3s = (await Account3Service.show()).data.account3s

    this.comp = this.$store.state.companyActiv ? this.$store.state.companyActiv : {}
    this.exRate = this.$store.state.defExRate ? this.$store.state.defExRate : 1

    if (!this.showEUR) {
      this.exRate = 1
    }
    this.currency = domacaValuta(this.$store.state.businessYear)
    this.lItems = [...this.items]
    this.balanceSh()
    this.gradiStablo()
  },
  computed: {
    // Redci koji se stvarno crtaju - cvor se vidi samo ako su mu svi preci otvoreni.
    vidljivi () {
      const out = []
      const hodaj = (cvorovi) => {
        cvorovi.forEach(n => {
          out.push(n)
          if (n.open) { hodaj(n.kids) }
        })
      }
      hodaj(this.stablo)
      return out
    },
    // Podnozje se racuna iz istog stabla iz kojeg se crtaju redci. Da se uzimao
    // Total1 iz balanceSh(), zbroj kolone i zbroj u podnozju mogli bi se
    // razlikovati za koji cent kad je tecaj razlicit od 1, jer balanceSh dijeli
    // s tecajem bez zaokruzivanja a redci se zaokruzuju na dvije decimale.
    zbroj () {
      const t = {psD: 0, psP: 0, prD: 0, prP: 0}
      this.stablo.forEach(n => {
        t.psD += n.psD
        t.psP += n.psP
        t.prD += n.prD
        t.prP += n.prP
      })
      t.totD = t.psD + t.prD
      t.totP = t.psP + t.prP
      t.saldo = t.totD - t.totP
      return t
    },
    uravnotezena () {
      return Math.abs(this.zbroj.saldo) < 0.005
    },
    razinaOpis () {
      return {1: 'klase', 2: 'sintetika', 3: 'sin.3', 4: 'sva konta'}[this.razina] || ''
    }
  },
  watch: {
    razina (nova) {
      this.postaviRazinu(nova)
    }
  },
  methods: {
    balanceSh() {
      this.psTotal1 = 0
      this.psTotal2 = 0
      this.yearTotal1 = 0
      this.yearTotal2 = 0
      this.Total1 = 0
      this.Total2 = 0
      this.Total3 = 0
      this.Total4 = 0
      let itLine = 0
      let id = 0
      let locId = 0

      if (this.lItems && this.account4Loc) {
        this.lItems.map(item => {
          itLine += 1
          id += 1

          const haveStart = this.psItems.find(psacc => psacc.account4 === item.account4)

          if (this.oneCond === null) {
            //header of class
            this.oneCond = item.account1
            const objectHeader = {
                id,
                itLine: 'KLASA',
                spaces: 0,
                oneCond: this.oneCond,
                addLine: true,
                onePsTotal1: null,
                onePsTotal2: null,
                oneYearTotal1: null,
                oneYearTotal2: null,
                oneTotal1: null,
                oneTotal2: null,
                account1Name: this.findA1(item.account1),
                CreditDebit: null,
                class: 'purple lighten-5',
                style: "font-weight:bold; font-size:20px",
                sirina: true,
                header: false
              }
              if (this.useYear === 1) {
                this.pItems.push(objectHeader)
              }
              if (this.useYear !== 1) {
                this.pOldItems.push(objectHeader)
              }
            id += 1
          }
          if (this.twoCond === null) {
            //header of syntetic
            this.twoCond = item.account2
            const objectHeader = {
                id,
                itLine: 'Sintetika',
                spaces: 2,
                oneCond: this.twoCond,
                addLine: true,
                onePsTotal1: null,
                onePsTotal2: null,
                oneYearTotal1: null,
                oneYearTotal2: null,
                oneTotal1: null,
                oneTotal2: null,
                account1Name: this.findA2(item.account2),
                CreditDebit: null,
                class: 'green lighten-5',
                style: "font-weight:bold; font-size:16px",
                sirina: true,
                header: false
              }
              if (this.useYear === 1) {
                this.pItems.push(objectHeader)
              }
              if (this.useYear !== 1) {
                this.pOldItems.push(objectHeader)
              }
            id += 1
          }
          if (this.threeCond === null) {
            //header of syntetic 3
            this.threeCond = item.account3
            const objectHeader = {
                id,
                itLine: 'Sin.3',
                spaces: 4,
                oneCond: this.threeCond,
                addLine: true,
                onePsTotal1: null,
                onePsTotal2: null,
                oneYearTotal1: null,
                oneYearTotal2: null,
                oneTotal1: null,
                oneTotal2: null,
                account1Name: this.findA3(item.account3),
                CreditDebit: null,
                class: '',
                style: "font-style: italic;",
                sirina: true,
                header: false
              }
              if (this.useYear === 1) {
                this.pItems.push(objectHeader)
              }
              if (this.useYear !== 1) {
                this.pOldItems.push(objectHeader)
              }
            id += 1
          }
          // NEW A3 ------------------------------------
          if (item.account3 !== this.threeCond) {
              // total of class
              const object2 = {
                id,
                itLine: 'Sin3.ukupno:',
                spaces: 4,
                oneCond: this.threeCond,
                addLine: true,
                onePsTotal1: this.threePsTotal1,
                onePsTotal2: this.threePsTotal2,
                oneYearTotal1: this.threeYearTotal1,
                oneYearTotal2: this.threeYearTotal2,
                oneTotal1: this.threeTotal1,
                oneTotal2: this.threeTotal2,
                account1Name: this.findA3(this.threeCond),
                CreditDebit: this.threeTotal1 - this.threeTotal2,
                class: '',
                style: "font-style: italic;",
                sirina: false,
                header: false
                
              }
              const object3 = {...item, ...object2}
              if (this.useYear === 1) {
                this.pItems.push(object3)
              }
              if (this.useYear !== 1) {
                this.pOldItems.push(object3)
              }
              itLine += 1
              id += 1
              this.threePsTotal1 = 0
              this.threePsTotal2 = 0
              this.threeYearTotal1 = 0
              this.threeYearTotal2 = 0
              this.threeTotal1 = 0
              this.threeTotal2 = 0
              locId = 0
  
          }
          // NEW A2 ------------------------------------
          if (item.account2 !== this.twoCond) {
              // total of class
              const object2 = {
                id,
                itLine: 'Sin.ukupno:',
                spaces: 2,
                oneCond: this.twoCond,
                addLine: true,
                onePsTotal1: this.twoPsTotal1,
                onePsTotal2: this.twoPsTotal2,
                oneYearTotal1: this.twoYearTotal1,
                oneYearTotal2: this.twoYearTotal2,
                oneTotal1: this.twoTotal1,
                oneTotal2: this.twoTotal2,
                account1Name: this.findA2(this.twoCond),
                CreditDebit: this.twoTotal1 - this.twoTotal2,
                class: 'green lighten-5',
                style: "font-weight:bold; font-size:16px",
                sirina: false,
                header: false
              }
              const object3 = {...item, ...object2}
              if (this.useYear === 1) {
                this.pItems.push(object3)
              }
              if (this.useYear !== 1) {
                this.pOldItems.push(object3)
              }
              itLine += 1
              id += 1
              this.twoPsTotal1 = 0
              this.twoPsTotal2 = 0
              this.twoYearTotal1 = 0
              this.twoYearTotal2 = 0
              this.twoTotal1 = 0
              this.twoTotal2 = 0
              locId = 0
          }

          // NEW A1  ------------------------------------
          if (item.account1 !== this.oneCond) {
              // total of class
              const object2 = {
                id,
                itLine: 'KLASA UKUPNO:',
                spaces: 0,
                oneCond: this.oneCond,
                addLine: true,
                onePsTotal1: this.onePsTotal1,
                onePsTotal2: this.onePsTotal2,
                oneYearTotal1: this.oneYearTotal1,
                oneYearTotal2: this.oneYearTotal2,
                oneTotal1: this.oneTotal1,
                oneTotal2: this.oneTotal2,
                account1Name: this.findA1(this.oneCond),
                CreditDebit: this.oneTotal1 - this.oneTotal2,
                class: 'purple lighten-5',
                style: "font-weight:bold; font-size:20px",
                sirina: false,
                header: true
              }
              const object3 = {...item, ...object2}
              if (this.useYear === 1) {
                this.pItems.push(object3)
              }
              if (this.useYear !== 1) {
                this.pOldItems.push(object3)
              }
              itLine += 1
              id += 1
              this.onePsTotal1 = 0
              this.onePsTotal2 = 0
              this.oneYearTotal1 = 0
              this.oneYearTotal2 = 0
              this.oneTotal1 = 0
              this.oneTotal2 = 0
              locId = 0

          }
          
          if (item.account1 !== this.oneCond) {
            //header of class
            this.oneCond = item.account1
            const objectHeader = {
                id,
                itLine: 'Klasa',
                spaces: 0,
                oneCond: this.oneCond,
                addLine: true,
                onePsTotal1: null,
                onePsTotal2: null,
                oneYearTotal1: null,
                oneYearTotal2: null,
                oneTotal1: null,
                oneTotal2: null,
                account1Name: this.findA1(item.account1),
                CreditDebit: null,
                class: 'purple lighten-5',
                style: "font-weight:bold; font-size:20px",
                sirina: true,
                header: false
              }
              if (this.useYear === 1) {
                this.pItems.push(objectHeader)
              }
              if (this.useYear !== 1) {
                this.pOldItems.push(objectHeader)
              }
            id += 1
          }
          if (item.account2 !== this.twoCond) {
            //header of syntetic
            this.twoCond = item.account2
            const objectHeader = {
                id,
                itLine: 'Sintetika',
                spaces: 2,
                oneCond: this.twoCond,
                addLine: true,
                onePsTotal1: null,
                onePsTotal2: null,
                oneYearTotal1: null,
                oneYearTotal2: null,
                oneTotal1: null,
                oneTotal2: null,
                account1Name: this.findA2(item.account2),
                CreditDebit: null,
                class: 'green lighten-5',
                style: "font-weight:bold; font-size:16px",
                sirina: true,
                header: false
              }
              if (this.useYear === 1) {
                this.pItems.push(objectHeader)
              }
              if (this.useYear !== 1) {
                this.pOldItems.push(objectHeader)
              }
            id += 1
          }
          if (item.account3 !== this.threeCond) {
            //header of syntetic 3
            this.threeCond = item.account3
            const objectHeader = {
                id,
                itLine: 'Sin.3',
                spaces: 4,
                oneCond: this.threeCond,
                addLine: true,
                onePsTotal1: null,
                onePsTotal2: null,
                oneYearTotal1: null,
                oneYearTotal2: null,
                oneTotal1: null,
                oneTotal2: null,
                account1Name: this.findA3(item.account3),
                CreditDebit: null,
                class: '',
                style: "font-style: italic;",
                sirina: true,
                header: false
              }
              if (this.useYear === 1) {
                this.pItems.push(objectHeader)
              }
              if (this.useYear !== 1) {
                this.pOldItems.push(objectHeader)
              }
            id += 1
          }

          const ps1 = round(((haveStart && haveStart.totalDebit ? parseFloat(haveStart.totalDebit) : 0) / this.exRate), 2)
          const ps2 = round(((haveStart && haveStart.totalCredit ? parseFloat(haveStart.totalCredit) : 0) / this.exRate), 2)
          const year1 = round((round((item.totalDebit) , 2) / this.exRate) - ps1, 2)
          const year2 = round((round((item.totalCredit) , 2) / this.exRate) - ps2, 2)
          const total1 = round((item.totalDebit), 2)/ this.exRate
          const total2 = round((item.totalCredit), 2)/ this.exRate


          this.psTotal1 += ps1
          this.psTotal2 += ps2
          this.yearTotal1 += year1
          this.yearTotal2 += year2
          this.Total1 += total1
          this.Total2 += total2
          // this.Total3 += parseFloat(item.DebitForegin)
          // this.Total4 += parseFloat(item.CreditForegin)
          
          // account1 totals
          this.onePsTotal1 += ps1
          this.onePsTotal2 += ps2
          this.oneYearTotal1 += year1
          this.oneYearTotal2 += year2
          this.oneTotal1 += total1
          this.oneTotal2 += total2
          // this.oneTotal3 += parseFloat(item.DebitForegin)
          // this.oneTotal4 += parseFloat(item.CreditForegin)

          // account2 totals
          this.twoPsTotal1 += ps1
          this.twoPsTotal2 += ps2
          this.twoYearTotal1 += year1
          this.twoYearTotal2 += year2
          this.twoTotal1 += total1
          this.twoTotal2 += total2
          // this.twoTotal3 += parseFloat(item.DebitForegin)
          // this.twoTotal4 += parseFloat(item.CreditForegin)

          // account3 totals
          this.threePsTotal1 += ps1
          this.threePsTotal2 += ps2
          this.threeYearTotal1 += year1
          this.threeYearTotal2 += year2
          this.threeTotal1 += total1
          this.threeTotal2 += total2
          // this.threeTotal3 += parseFloat(item.DebitForegin)
          // this.threeTotal4 += parseFloat(item.CreditForegin)
          locId += 1
          const object2 = {
            id,
            locId,
            itLine ,
            addLine: false,
            account4Name: this.findA4(item.account4),
            psDebit: ps1,
            psCredit: ps2,
            yearDebit: year1,
            yearCredit: year2,
            totDebit: total1,
            totCredit: total2,
            CreditDebit: total1 - total2,
            acc4Total: total1 - total2,
            sirina: false,
            header: false
          }
          const object3 = {...item, ...object2}
          if (this.useYear === 1) {
            this.pItems.push(object3)
          }
          if (this.useYear !== 1) {
            this.pOldItems.push(object3)
          }
          this.asLast = {
            oneCond: this.oneCond,
            twoCond: this.twoCond,
            threeCond: this.threeCond,
            onePsTotal1: this.onePsTotal1,
            onePsTotal2: this.onePsTotal2,
            oneYearTotal1: this.oneYearTotal1,
            oneYearTotal2: this.oneYearTotal2,
            oneTotal1: parseNum(this.onePsTotal1) + parseNum(this.oneYearTotal1),
            oneTotal2: parseNum(this.onePsTotal2) + parseNum(this.oneYearTotal2),

            twoPsTotal1: this.twoPsTotal1,
            twoPsTotal2: this.twoPsTotal2,
            twoYearTotal1: this.twoYearTotal1,
            twoYearTotal2: this.twoYearTotal2,
            twoTotal1: parseNum(this.twoPsTotal1) + parseNum(this.twoYearTotal1),
            twoTotal2: parseNum(this.twoPsTotal2) + parseNum(this.twoYearTotal2),

            threePsTotal1: this.threePsTotal1,
            threePsTotal2: this.threePsTotal2,
            threeYearTotal1: this.threeYearTotal1,
            threeYearTotal2: this.threeYearTotal2,
            threeTotal1: parseNum(this.threePsTotal1) + parseNum(this.threeYearTotal1),
            threeTotal2: parseNum(this.threePsTotal2) + parseNum(this.threeYearTotal2),
            sirina: false,
            header: false
          } 
        })
      }
      this.Total5 = this.Total1 - this.Total2

      const addLastOne = {
        account1Name: this.asLast.oneCond ? this.findA1(this.asLast.oneCond) : '',
        account2Name: this.asLast.twoCond ? this.findA2(this.asLast.twoCond) : '',
        account3Name: this.asLast.threeCond ? this.findA3(this.asLast.threeCond) : '',

        onePsSum: this.asLast.onePsTotal1 - this.asLast.onePsTotal2 ,
        oneYearSum: this.asLast.oneYearTotal1 - this.asLast.oneYearTotal2,
        oneSum:  this.asLast.oneTotal1 - this.asLast.oneTotal2,

        twoPsSum: this.asLast.twoPsTotal1 - this.asLast.twoPsTotal2,
        twoYearSum: this.asLast.twoYearTotal1 - this.asLast.twoYearTotal2,
        twoSum:  this.asLast.twoTotal1 - this.asLast.twoTotal2 ,

        threePsSum: this.asLast.threePsTotal1 - this.asLast.threePsTotal2,
        threeYearSum: this.asLast.threeYearTotal1 - this.asLast.threeYearTotal2,
        threeSum:  this.asLast.threeTotal1 - this.asLast.threeTotal2
      }
      this.lastOne ={ ...this.asLast, ...addLastOne}
      this.balance = {...this.head}
      if (this.useYear === 1) {
        this.balance.items = this.pItems
      }
      if (this.useYear !== 1) {
        this.balance.items = this.pOldItems
      }
      this.balance = {...this.balance, ...this.lastOne}
      this.balance.psTotal1 = this.psTotal1
      this.balance.psTotal2 = this.psTotal2
      this.balance.yearTotal1 = this.yearTotal1
      this.balance.yearTotal2 = this.yearTotal2
      this.balance.Total1 = this.Total1
      this.balance.Total2 = this.Total2
      this.balance.Total5 = this.Total5
      this.balance.showEUR = this.showEUR
    },
    // ---------------------------------------------------------------------
    // Stablo za prikaz na ekranu.
    //
    // balanceSh() gradi pItems - ravnu listu u kojoj su pomijesani redci konta,
    // zaglavlja grupa i zbrojevi. Ta lista i dalje ide u print, Excel, RDG i
    // GFI-POD i ne dira se. Za ekran se gradi zasebno stablo jer prikaz po
    // razinama i sklapanje grupa trebaju roditelja i djecu, ne ravnu listu.
    //
    // Brojke se racunaju istom matematikom kao u balanceSh() da se ekran i ispis
    // ne mogu razici: ps = pocetno stanje, promet = razdoblje bez pocetnog
    // stanja, ukupno = zbroj to dvoje.
    // ---------------------------------------------------------------------
    gradiStablo () {
      const korijen = []
      const idx = {}
      let konta = 0

      const noviCvor = (code, level, name, kljuc) => ({
        key: kljuc,
        code: code,
        level: level,
        name: name,
        psD: 0, psP: 0, prD: 0, prP: 0,
        kids: [],
        open: false
      })

      const items = this.lItems && this.lItems.length > 0 ? this.lItems : []

      items.forEach(item => {
        const haveStart = this.psItems
          ? this.psItems.find(psacc => psacc.account4 === item.account4)
          : null

        const ps1 = round(((haveStart && haveStart.totalDebit ? parseFloat(haveStart.totalDebit) : 0) / this.exRate), 2)
        const ps2 = round(((haveStart && haveStart.totalCredit ? parseFloat(haveStart.totalCredit) : 0) / this.exRate), 2)
        const year1 = round((round((item.totalDebit), 2) / this.exRate) - ps1, 2)
        const year2 = round((round((item.totalCredit), 2) / this.exRate) - ps2, 2)

        const a1 = item.account1 ? String(item.account1) : ''
        const a2 = item.account2 ? String(item.account2) : ''
        const a3 = item.account3 ? String(item.account3) : ''
        const a4 = item.account4 ? String(item.account4) : ''

        // Kljuc nosi cijelu putanju - sama sifra sintetike nije jedinstvena
        // ako kontni plan nije dosljedno numeriran.
        const razine = [
          {code: a1, level: 1, name: this.findA1(item.account1), kljuc: a1},
          {code: a2, level: 2, name: this.findA2(item.account2), kljuc: a1 + '|' + a2},
          {code: a3, level: 3, name: this.findA3(item.account3), kljuc: a1 + '|' + a2 + '|' + a3}
        ]

        let lista = korijen
        razine.forEach(r => {
          let cvor = idx[r.kljuc]
          if (!cvor) {
            cvor = noviCvor(r.code, r.level, r.name, r.kljuc)
            idx[r.kljuc] = cvor
            lista.push(cvor)
          }
          cvor.psD += ps1
          cvor.psP += ps2
          cvor.prD += year1
          cvor.prP += year2
          lista = cvor.kids
        })

        const kljuc4 = a1 + '|' + a2 + '|' + a3 + '|' + a4
        let list = idx[kljuc4]
        if (!list) {
          list = noviCvor(a4, 4, this.findA4(item.account4), kljuc4)
          idx[kljuc4] = list
          lista.push(list)
          konta += 1
        }
        list.psD += ps1
        list.psP += ps2
        list.prD += year1
        list.prP += year2
      })

      // Iznosi se formatiraju jednom, kod gradnje - redaka zna biti vise stotina
      // pa se ne racuna u svakom crtanju.
      const zavrsi = (cvorovi) => {
        cvorovi.forEach(n => {
          n.totD = n.psD + n.prD
          n.totP = n.psP + n.prP
          n.saldo = n.totD - n.totP
          n.fPsD = this.iznos(n.psD)
          n.fPsP = this.iznos(n.psP)
          n.fPrD = this.iznos(n.prD)
          n.fPrP = this.iznos(n.prP)
          n.fTotD = this.iznos(n.totD)
          n.fTotP = this.iznos(n.totP)
          n.fSaldo = Math.abs(n.saldo) < 0.005 ? '–' : this.formatPrice(Math.abs(n.saldo))
          n.dp = Math.abs(n.saldo) < 0.005 ? '' : (n.saldo > 0 ? 'D' : 'P')
          n.negativan = n.saldo < 0
          zavrsi(n.kids)
        })
      }
      zavrsi(korijen)

      this.brojKonta = konta
      this.stablo = korijen
      this.postaviRazinu(this.razina)
    },
    iznos (v) {
      if (!v || Math.abs(v) < 0.005) { return '–' }
      return this.formatPrice(v)
    },
    postaviRazinu (dubina) {
      const postavi = (cvorovi) => {
        cvorovi.forEach(n => {
          n.open = n.level < dubina
          postavi(n.kids)
        })
      }
      postavi(this.stablo)
    },
    prebaci (cvor) {
      cvor.open = !cvor.open
    },
    klikabilan (cvor) {
      return this.prikaz === 'detaljno' && cvor.level === 4
    },
    klikRed (cvor) {
      if (!this.klikabilan(cvor)) { return }
      this.$refs.kartica.open({
        account4: cvor.code,
        account4Name: cvor.name,
        psDebit: cvor.psD,
        psCredit: cvor.psP,
        prometDebit: cvor.prD,
        prometCredit: cvor.prP,
        head: this.head,
        exRate: this.exRate,
        currency: this.currency
      })
    },
    // Na papir ide ono sto je na ekranu - ista razina, isti skup kolona.
    print(){
      printBalancePdf({
        redci: this.vidljivi,
        zbroj: this.zbroj,
        detaljno: this.prikaz === 'detaljno',
        razinaOpis: this.razinaOpis,
        company: this.comp,
        fromDate: this.head.docFromDate,
        toDate: this.head.docToDate,
        fromAccount: this.head.fromAccount,
        toAccount: this.head.toAccount,
        valuta: this.currency,
        brojKonta: this.brojKonta
      })
    },
    findA1(condition) {
      const account1Name = this.account1s.filter(account => {
        return account.account1 === condition
      })
      return (account1Name && account1Name.length > 0 && account1Name[0].account1Description ? account1Name[0].account1Description : '')
    },
    findA2(condition) {
      const account2Name = this.account2s.filter(account => {
        return account.account2 === condition
      })
      return (account2Name && account2Name.length > 0  && account2Name[0].account2Description ? account2Name[0].account2Description : '')
    },
    findA3(condition) {
      const account3Name = this.account3s.filter(account => {
        return account.account3 === condition
      })
      return (account3Name && account3Name.length > 0 && account3Name[0].account3Description ? account3Name[0].account3Description : '')
    },
    findA4(condition) {
      const account4Name = this.account4Loc.filter(account => {
        return account.account4 === condition
      })
      return (account4Name && account4Name.length > 0 && account4Name[0].account4Description ? account4Name[0].account4Description : '')
    },
    formatPrice (value) {
      if (this.$store.state.language === 'EN') {
        let val = (value / 1).toFixed(2).replace('.', ',')
        return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')
      }
      if (this.$store.state.language === 'HR') {
        let val = (value / 1).toFixed(2).replace('.', ',')
        return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')
      }
    },
    formatDate (date) {
      if (!date) {
        return null
      }
      const [year, month, day] = date.split('-')
      return `${day}.${month}.${year}`
    },
    localDate(dateToFormat) {
       return dateFormat(dateToFormat)
    },
    parseDate (date) {
      if (!date) {
        return null
      }
      const [day, month, year] = date.split('.')
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
    },
    localParse(parseNumber) {
      return  parseNum(parseNumber)
    },

    async rdg(type) {

      const emp = []
      this.pOldItems = [...emp]
      const mlAnaPY = this.$store.state.searchState ? {...this.$store.state.searchState} : {}
      const newDocFromDate = mlAnaPY.docFromDate ? (parseInt(mlAnaPY.docFromDate.substr(0,4)) - 1).toString() + mlAnaPY.docFromDate.substr(4,6) : ''
      const newDocToDate = mlAnaPY.docToDate ? (parseInt(mlAnaPY.docToDate.substr(0,4)) - 1).toString() + mlAnaPY.docToDate.substr(4,6) : ''
      
      mlAnaPY.docFromDate = newDocFromDate
      mlAnaPY.docToDate = newDocToDate
      mlAnaPY.BusinessYear = parseInt(mlAnaPY.docFromDate.substr(0,4))
      mlAnaPY.type = 1
      this.exRate = round(mlAnaPY.BusinessYear,0) === 2022 ? 7.5345 : 1
      if (mlAnaPY.bookTypeSelected) {
        delete mlAnaPY.bookTypeSelected
      }
      if (mlAnaPY.subtype) {
        delete mlAnaPY.subtype
      }
      if (mlAnaPY.BookTypeID) {
        delete mlAnaPY.BookTypeID
      }

      await MainLedgerDetailService.card(mlAnaPY)
      .then (res => {
          this.pOldItems = res && res.data && res.data.documents ? res.data.documents : []
          if (this.pOldItems && this.pOldItems.length > 0) {
            this.pOldItems.map(pItem => {
              const lInd = this.pOldItems.indexOf(pItem)
              const CreditDebit = round(pItem.totalDebit,2) - round(pItem.totalCredit,2)
              this.pOldItems[lInd].totalDebit = round(pItem.totalDebit / this.exRate,2)
              this.pOldItems[lInd].totalCredit = round(pItem.totalCredit / this.exRate,2)
              this.pOldItems[lInd].CreditDebit = round(CreditDebit/ this.exRate, 2)
            })
          }
          this.useYear = 2
          //this.balanceSh()
          
          for (var member in this.fRDG) delete this.fRDG[member]
          if (type === 1){
            this.rdg2()
          }
          if (type === 2){
            console.log('0 - radim gfi pod')
            this.fullGFI.length = 0
            const sendTo = {}
            sendTo.pItems = this.pItems
            sendTo.pOldItems = this.pOldItems

            const newGFI = doAll(sendTo)
            this.fullGFI = [...newGFI]
            this.gfipodPrint()
          }

          this.useYear = 1
        }
      )
      .catch(err => {
           this.error = 'Error  ' + err
        })
    },


    gfipodPrint() {
      const fRDG = {}
      fRDG.head = {...this.head}
      fRDG.items = [...this.fullGFI]
      // Kao i RDG - prvo se prikaze, pa se iz prikaza ispisuje ili izvozi.
      this.$refs.izvjestaj.open(fRDG, this.currency, 'GFI-POD Bilanca')
    },
    // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
    // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
    // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>

    rdg2() {
      const fullRDG =[]
      const emptyObj = {}
      this.nI= {...emptyObj}
      let poslovniPrihodiPg = 0
      let poslovniPrihodiTg = 0

      // ----------------------------------------------------------------POSLOVNI PRIHODI
      this.nI.id = 1
      this.nI.header = true
      this.nI.bold = true
      this.nI.small = false
      this.nI.col1 = 'I'
      this.nI.col2 = '75,76 i 78 dio'
      this.nI.col3 = 'POSLOVNI PRIHODI (1+2+3)'
      this.nI.col4 = ''
      this.nI.col5 = null
      this.nI.col6 = null

      this.nA = { ...emptyObj}

      let col5 = 0
      let col6 = 0
      const res1 = this.doRdgCalc(1, '750', '754', 'oneTotal2', 'totalCredit')
      if (res1.prevYear) {col5 += res1.prevYear }
      if (res1.currYear) {col6 += res1.currYear }
      
      const res2 = this.doRdgCalc(1, '756', '759', 'oneTotal2', 'totalCredit')
      if (res2.prevYear) {col5 += res2.prevYear }
      if (res2.currYear) {col6 += res2.currYear }
      // col5 = res2.prevYear ? col5 + res2.prevYear : col5
      // col6 = res2.currYear ? col6 + res2.currYear : col6

      const res3 = this.doRdgCalc(1, '760', '764', 'oneTotal2', 'totalCredit')
      if (res3.prevYear) {col5 += res3.prevYear }
      if (res3.currYear) {col6 += res3.currYear }
      // col5 = res3.prevYear ? col5 + res3.prevYear : col5
      // col6 = res3.currYear ? col6 + res3.currYear : col6

      const res4 = this.doRdgCalc(1, '766', '769', 'oneTotal2', 'totalCredit')
      if (res4.prevYear) {col5 += res4.prevYear }
      if (res4.currYear) {col6 += res4.currYear }
      // col5 = res4.prevYear ? col5 + res4.prevYear : col5
      // col6 = res4.currYear ? col6 + res4.currYear : col6
  

      this.nA.id = 2
      this.nA.header = false
      this.nA.bold = true
      this.nA.small = false
      this.nA.col1 = '1.'
      this.nA.col2 = '750 do 754, 756 do 759, 760 do 764, 766 do 769'
      this.nA.col3 = 'Prihodi od prodaje'
      this.nA.col4 = ''
      this.nA.col5 = col5
      this.nA.col6 = col6
      poslovniPrihodiPg += col5
      poslovniPrihodiTg += col6

      this.nB= {...emptyObj}

      col5 = 0
      col6 = 0

      const res5 = this.doRdgCalc(2, '755', '', 'oneTotal2', 'totalCredit')
      col5 = res5.prevYear ? col5 + res5.prevYear : col5
      col6 = res5.currYear ? col6 + res5.currYear : col6

      const res6 = this.doRdgCalc(2, '765', '', 'oneTotal2', 'totalCredit')
      col5 = res6.prevYear ? col5 + res6.prevYear : col5
      col6 = res6.currYear ? col6 + res6.currYear : col6

      this.nB.id = 3
      this.nB.header = false
      this.nB.bold = true
      this.nB.small = false
      this.nB.col1 = '2.'
      this.nB.col2 = '755 i 765'
      this.nB.col3 = 'Prihod na temelju uporabe vlastitih proizvoda, roba i usluga'
      this.nB.col4 = ''
      this.nB.col5 = col5
      this.nB.col6 = col6
      poslovniPrihodiPg += col5
      poslovniPrihodiTg += col6

      this.nC= {...emptyObj}
      col5 = 0
      col6 = 0

      const res7 = this.doRdgCalc(1, '780', '786', 'oneTotal2', 'totalCredit')
      col5 = res7.prevYear ? col5 + res7.prevYear : col5
      col6 = res7.currYear ? col6 + res7.currYear : col6

      this.nC.id = 4
      this.nC.header = false
      this.nC.bold = true
      this.nC.small = false
      this.nC.col1 = '3.'
      this.nC.col2 = '780 do 786'
      this.nC.col3 = 'Ostali poslovni prihodi'

      this.nC.col4 = ''
      this.nC.col5 = col5
      this.nC.col6 = col6
      poslovniPrihodiPg += col5
      poslovniPrihodiTg += col6

      this.nI.col5 = poslovniPrihodiPg
      this.nI.col6 = poslovniPrihodiTg
      fullRDG.push(this.nI)
      fullRDG.push(this.nA)
      fullRDG.push(this.nB)
      fullRDG.push(this.nC)


      // ----------------------------------------------------------------POSLOVNI RASHODI
      this.nI= {...emptyObj}

      this.nI.id = 5
      this.nI.header = true
      this.nI.bold = true
      this.nI.small = false
      this.nI.col1 = 'II'
      this.nI.col2 = ''
      this.nI.col3 = 'POSLOVNI RASHODI (1 + 3 do 9 ili 3 do 9 - 2)'
      this.nI.col4 = ''
      this.nI.col5 = null
      this.nI.col6 = null
      const poslovniRashodiIndex = fullRDG.push(this.nI)
      
      col5 = 0
      col6 = 0

      const res8 = this.doRdgCalc(3, '60', '64', 'oneTotal2', 'totalCredit')
      col5 = res8.prevYear ? col5 + res8.prevYear : col5
      col6 = res8.currYear ? col6 + res8.currYear : col6

      this.nI= {...emptyObj}
      this.nI.id = 6
      this.nI.header = false
      this.nI.bold = true
      this.nI.small = false
      this.nI.col1 = '1.'
      this.nI.col2 = '60 do 64'
      this.nI.col3 = 'Smanjenje vrijednosti zaliha nedovršene proizvodnje i gotovih proizvoda'
      this.nI.col4 = ''
      this.nI.col5 = col5 && col5 < 0 ? res8.prevYear : 0
      this.nI.col6 = col6 && col6 < 0 ? res8.currYear : 0
      const rashod1pg = this.nI.col5
      const rashod1tg = this.nI.col6
      fullRDG.push(this.nI)


      this.nI= {...emptyObj}
      this.nI.id = 7
      this.nI.header = false
      this.nI.bold = true
      this.nI.small = false
      this.nI.col1 = '2.'
      this.nI.col2 = '60 do 64'
      this.nI.col3 = 'Povećanje vrijednosti zaliha nedovršene proizvodnje i gotovih proizvoda'
      this.nI.col4 = ''
      this.nI.col5 = col5 && col5 > 0 ? res8.prevYear : 0
      this.nI.col6 = col6 && col6 > 0 ? res8.currYear : 0
      const rashod2pg = this.nI.col5
      const rashod2tg = this.nI.col6
      fullRDG.push(this.nI)


      // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>

      col5 = 0
      col6 = 0
      

      this.nI= {...emptyObj}
      this.nI.id = 8
      this.nI.header = false
      this.nI.bold = true
      this.nI.small = false
      this.nI.col1 = ''
      this.nI.col2 = '40, 41, 703 i 71'
      this.nI.col3 = 'Materijalni troškovi (a + b + c)'
      this.nI.col4 = ''
           
      const res9 = this.doRdgCalc(4, "40",'', 'oneTotal1', 'totalDebit')
      col5 = res9.prevYear ? col5 + res9.prevYear : col5
      col6 = res9.currYear ? col6 + res9.currYear : col6

      const res11 = this.doRdgCalc(2, "703",'', 'oneTotal1', 'totalDebit')
      col5 = res11.prevYear ? col5 + res11.prevYear : col5
      col6 = res11.currYear ? col6 + res11.currYear : col6

      this.nA= {...emptyObj}
      this.nA.id = 9
      this.nA.header = false
      this.nA.bold = false
      this.nA.small = false
      this.nA.col1 = ''
      this.nA.col2 = '40 i 703'
      this.nA.col3 = '\t a) Troškovi sirovna i materijala'
      this.nA.col4 = ''
      this.nA.col5 = col5 ? col5 : 0
      this.nA.col6 = col6 ? col6 : 0

      col5 = 0
      col6 = 0
      
      const res12 = this.doRdgCalc(4, "71",'', 'oneTotal1', 'totalDebit')
      col5 = res12.prevYear ? res12.prevYear : 0
      col6 = res12.currYear ? res12.currYear : 0
      const res13 = this.doRdgCalc(2, "718",'', 'oneTotal1', 'totalDebit') //??
      col5 = res13.prevYear ? col5 - res13.prevYear : col5
      col6 = res13.currYear ? col6 - res13.currYear : col6

      this.nB= {...emptyObj}
      this.nB.id = 10
      this.nB.header = false
      this.nB.bold = false
      this.nB.small = false
      this.nB.col1 = '3.'
      this.nB.col2 = '71 (osim 718)'
      this.nB.col3 = '\t b) Troškovi prodane robe'
      this.nB.col4 = ''
      this.nB.col5 = col5 ? col5 : 0
      this.nB.col6 = col6 ? col6 : 0
      
      const res14 = this.doRdgCalc(4, "41",'', 'oneTotal1', 'totalDebit')
      
      this.nC= {...emptyObj}
      this.nC.id = 10
      this.nC.header = false
      this.nC.bold = false
      this.nC.small = false
      this.nC.col1 = ''
      this.nC.col2 = '41'
      this.nC.col3 = '\t c) Ostali vanjski troškovi'
      this.nC.col4 = ''
      this.nC.col5 = res14.prevYear ? res14.prevYear : 0
      this.nC.col6 = res14.currYear ? res14.currYear : 0   

      this.nI.col5 = this.nA.col5 + this.nB.col5 + this.nC.col5
      this.nI.col6 = this.nA.col6 + this.nB.col6 + this.nC.col6
      const rashod3pg = this.nA.col5 + this.nB.col5 + this.nC.col5
      const rashod3tg = this.nA.col6 + this.nB.col6 + this.nC.col6
      fullRDG.push(this.nI)
      fullRDG.push(this.nA)
      fullRDG.push(this.nB)
      fullRDG.push(this.nC)



      this.nI= {...emptyObj}
      this.nI.id = 11
      this.nI.header = false
      this.nI.bold = true
      this.nI.small = false
      this.nI.col1 = ''
      this.nI.col2 = '42'
      this.nI.col3 = 'Troškovi osoblja (a + b + c)'
      this.nI.col4 = ''
      this.nI.col5 = null
      this.nI.col6 = null

      col5 = 0
      col6 = 0
      
      const res15 = this.doRdgCalc(2, "420",'', 'oneTotal1', 'totalDebit')
      this.nA= {...emptyObj}
      this.nA.id = 12
      this.nA.header = false
      this.nA.bold = false
      this.nA.small = false
      this.nA.col1 = ''
      this.nA.col2 = '420'
      this.nA.col3 = '\t  a) Neto plaće i nadnice'
      this.nA.col4 = ''
      this.nA.col5 = res15.prevYear ? res15.prevYear : 0
      this.nA.col6 = res15.currYear ? res15.currYear : 0
      
      const res16 = this.doRdgCalc(2, "421",'', 'oneTotal1', 'totalDebit')
      this.nB= {...emptyObj}
      this.nB.id = 13
      this.nB.header = false
      this.nB.bold = false
      this.nB.small = false
      this.nB.col1 = '4.'
      this.nB.col2 = '421'
      this.nB.col3 = '\t  b) Troškovi poreza i doprinosi iz plaća'
      this.nB.col4 = ''
      this.nB.col5 = res16.prevYear ? res16.prevYear : 0
      this.nB.col6 = res16.currYear ? res16.currYear : 0
      
      const res17 = this.doRdgCalc(2, "422")
      this.nC= {...emptyObj}
      this.nC.id = 14
      this.nC.header = false
      this.nC.bold = false
      this.nC.small = false
      this.nC.col1 = ''
      this.nC.col2 = '422'
      this.nC.col3 = '\t  c) Doprinosi na plaće'
      this.nC.col4 = ''
      this.nC.col5 = res17.prevYear ? res17.prevYear : 0
      this.nC.col6 = res17.currYear ? res17.currYear : 0
      

      this.nI.col5 = this.nA.col5 + this.nB.col5 + this.nC.col5
      this.nI.col6 = this.nA.col6 + this.nB.col6 + this.nC.col6
      const rashod4pg = this.nA.col5 + this.nB.col5 + this.nC.col5
      const rashod4tg = this.nA.col6 + this.nB.col6 + this.nC.col6
      fullRDG.push(this.nI)
      fullRDG.push(this.nA)
      fullRDG.push(this.nB)
      fullRDG.push(this.nC)

      
      const res18 = this.doRdgCalc(4, "43",'', 'oneTotal1', 'totalDebit')
      this.nI= {...emptyObj}
      this.nI.id = 15
      this.nI.header = false
      this.nI.bold = true
      this.nI.small = false
      this.nI.col1 = '5.'
      this.nI.col2 = '43'
      this.nI.col3 = 'Amortizacija'
      this.nI.col4 = ''
      this.nI.col5 = res18.prevYear ? res18.prevYear : 0
      this.nI.col6 = res18.currYear ? res18.currYear : 0
      const rashod5pg = this.nI.col5
      const rashod5tg = this.nI.col6
      fullRDG.push(this.nI)

      // Skupina 46 dijeli se izmedju ovog reda i reda 9. Ovdje su redovni vanjski
      // troskovi (460 do 465 i 469), a pristojbe, osiguranja i naknade (466 do 468)
      // idu u red 9. Prije je oba reda uzimala cijelu skupinu pa se 46 dvaput
      // zbrajalo u poslovne rashode.
      col5 = 0
      col6 = 0
      const res19 = this.doRdgCalc(1, '460', '465', 'oneTotal1', 'totalDebit')
      if (res19.prevYear) { col5 += res19.prevYear }
      if (res19.currYear) { col6 += res19.currYear }

      const res19b = this.doRdgCalc(2, '469', '', 'oneTotal1', 'totalDebit')
      if (res19b.prevYear) { col5 += res19b.prevYear }
      if (res19b.currYear) { col6 += res19b.currYear }

      this.nI= {...emptyObj}
      this.nI.id = 16
      this.nI.header = false
      this.nI.bold = true
      this.nI.small = false
      this.nI.col1 = '6.'
      this.nI.col2 = '460 do 465 i 469'
      this.nI.col3 = 'Ostali troškovi'
      this.nI.col4 = ''
      this.nI.col5 = col5
      this.nI.col6 = col6
      const rashod6pg = this.nI.col5
      const rashod6tg = this.nI.col6
      fullRDG.push(this.nI)


      this.nI= {...emptyObj}
      this.nI.id = 17
      this.nI.header = false
      this.nI.bold = true
      this.nI.small = false
      this.nI.col1 = ''
      this.nI.col2 = '44, 708 i 718'
      this.nI.col3 = 'Vrijednosno usklađivanje (a + b)'
      this.nI.col4 = ''

      const res20 = this.doRdgCalc(1, '440', '442', 'oneTotal1', 'totalDebit')
      this.nA= {...emptyObj}
      this.nA.id = 18
      this.nA.header = false
      this.nA.bold = false
      this.nA.small = false
      this.nA.col1 = '7.'
      this.nA.col2 = '440 do 442'
      this.nA.col3 = '\t  a) Vrijednosno usklađivanje dugotrajne imovine (osim financijske imovine)'
      this.nA.col4 = ''
      this.nA.col5 = res20.prevYear ? res20.prevYear : 0
      this.nA.col6 = res20.currYear ? res20.currYear : 0
      
      col5 = 0
      col6 = 0
      const res21 = this.doRdgCalc(1, '444', '447', 'oneTotal1', 'totalDebit')
      col5 = res21.prevYear ? col5 + res21.prevYear : col5
      col6 = res21.currYear ? col6 + res21.currYear : col6

      const res22 = this.doRdgCalc(2, "718",'', 'oneTotal1', 'totalDebit') //?
      col5 = res22.prevYear ? col5 + res22.prevYear : col5
      col6 = res22.currYear ? col6 + res22.currYear : col6
      this.nB= {...emptyObj}
      this.nB.id = 19
      this.nB.header = false
      this.nB.bold = false
      this.nB.small = false
      this.nB.col1 = ''
      this.nB.col2 = '444 do 447 i 718'
      this.nB.col3 = '\t  b) Vrijednosno usklađivanje kratkotrajne imovine (osim financijske imovine)'
      this.nB.col4 = ''
      this.nB.col5 = col5
      this.nB.col6 = col6
      
      this.nI.col5 = this.nA.col5 + this.nB.col5 
      this.nI.col6 = this.nA.col6 + this.nB.col6
      const rashod7pg = this.nI.col5
      const rashod7tg = this.nI.col6
      fullRDG.push(this.nI)
      fullRDG.push(this.nA)
      fullRDG.push(this.nB)


      const res23 = this.doRdgCalc(4, "45",'', 'oneTotal1', 'totalDebit')
      this.nI= {...emptyObj}
      this.nI.id = 20
      this.nI.header = false
      this.nI.bold = true
      this.nI.small = false
      this.nI.col1 = '8.'
      this.nI.col2 = '45'
      this.nI.col3 = 'Rezerviranja'
      this.nI.col4 = ''
      this.nI.col5 = res23.prevYear ? res23.prevYear : 0
      this.nI.col6 = res23.currYear ? res23.currYear : 0
      const rashod8pg = this.nI.col5
      const rashod8tg = this.nI.col6
      fullRDG.push(this.nI)

      col5 = 0
      col6 = 0
      // "46 dio" iz obrasca = 466 do 468 (osiguranja, bankarske naknade,
      // clanarine i pristojbe). Ostatak skupine 46 je u redu 6.
      const res24 = this.doRdgCalc(1, '466', '468', 'oneTotal1', 'totalDebit')
      col5 = res24.prevYear ? col5 + res24.prevYear : col5
      col6 = res24.currYear ? col6 + res24.currYear : col6

      const res25 = this.doRdgCalc(4, "48",'', 'oneTotal1', 'totalDebit')
      col5 = res25.prevYear ? col5 + res25.prevYear : col5
      col6 = res25.currYear ? col6 + res25.currYear : col6

      const res26 = this.doRdgCalc(1, '704', '707', 'oneTotal1', 'totalDebit') //?
      col5 = res26.prevYear ? col5 + res26.prevYear : col5
      col6 = res26.currYear ? col6 + res26.currYear : col6

      const res27 = this.doRdgCalc(1, '713', '718', 'oneTotal1', 'totalDebit')
      col5 = res27.prevYear ? col5 + res27.prevYear : col5
      col6 = res27.currYear ? col6 + res27.currYear : col6

      const res28 = this.doRdgCalc(2, "721",'', 'oneTotal1', 'totalDebit') //?
      col5 = res28.prevYear ? col5 + res28.prevYear : col5
      col6 = res28.currYear ? col6 + res28.currYear : col6


      this.nI= {...emptyObj}
      this.nI.id = 21
      this.nI.header = false
      this.nI.bold = true
      this.nI.small = false
      this.nI.col1 = '9.'
      this.nI.col2 = '46 dio 48, 704 do 707, 713 do 718 i 721'
      this.nI.col3 = 'Ostali poslovni rashodi'
      this.nI.col4 = ''
      this.nI.col5 = col5
      this.nI.col6 = col6
      const rashod9pg = this.nI.col5
      const rashod9tg = this.nI.col6
      fullRDG.push(this.nI)

      let poslovniRashodiPg = rashod3pg + rashod4pg + rashod5pg + rashod6pg + rashod7pg + rashod8pg + rashod9pg
      let poslovniRashodiTg = rashod3tg + rashod4tg + rashod5tg + rashod6tg + rashod7tg + rashod8tg + rashod9tg
      if (rashod1pg > 0) {
        poslovniRashodiPg += rashod1pg  
      }
      if (rashod2pg > 0) {
        poslovniRashodiPg -= rashod2pg
      }
      if (rashod1tg > 0) {
        poslovniRashodiTg += rashod1tg
      }
      if (rashod2tg > 0) {
        poslovniRashodiTg -= rashod2tg
      }
      fullRDG[poslovniRashodiIndex-1].col5 = poslovniRashodiPg
      fullRDG[poslovniRashodiIndex-1].col6 = poslovniRashodiTg

      // -------------------------------------------------------------FINANCIJSKI PRIHODI
      let financijskiPrihodiPg = 0
      let financijskiPrihodiTg = 0
      this.nI= {...emptyObj}
      this.nI.id = 22
      this.nI.header = true
      this.nI.bold = true
      this.nI.small = false
      this.nI.col1 = 'III'
      this.nI.col2 = '77'
      this.nI.col3 = 'FINANCIJSKI PRIHODI (1 do 5)'
      this.nI.col4 = ''
      this.nI.col5 = 0
      this.nI.col6 = 0
      
      const financijskiPrihodiIndex = fullRDG.push(this.nI)

      const res30 = this.doRdgCalc(2, "770",'', 'oneTotal2', 'totalCredit')
      this.nI= {...emptyObj}
      this.nI.id = 23
      this.nI.header = false
      this.nI.bold = true
      this.nI.small = false
      this.nI.col1 = '1.'
      this.nI.col2 = '770'
      this.nI.col3 = 'Kamate, tečajne razlike, dividende i slični prihodi iz odnosa s povezanim poduzetnicima'
      this.nI.col4 = ''
      this.nI.col5 = res30.prevYear ? res30.prevYear : 0
      this.nI.col6 = res30.currYear ? res30.currYear : 0
      financijskiPrihodiPg = this.nI.col5
      financijskiPrihodiTg = this.nI.col6
      fullRDG.push(this.nI)

      col5 = 0
      col6 = 0
      const res31 = this.doRdgCalc(2, "771",'', 'oneTotal2', 'totalCredit')
      col5 = res31.prevYear ? col5 + res31.prevYear : col5
      col6 = res31.currYear ? col6 + res31.currYear : col6

      const res32 = this.doRdgCalc(2, "772",'', 'oneTotal2', 'totalCredit')
      col5 = res32.prevYear ? col5 + res32.prevYear : col5
      col6 = res32.currYear ? col6 + res32.currYear : col6

      const res33 = this.doRdgCalc(2, "773",'', 'oneTotal2', 'totalCredit')
      col5 = res33.prevYear ? col5 + res33.prevYear : col5
      col6 = res33.currYear ? col6 + res33.currYear : col6

      // Skupina 774 dijeli se s redom 5. Ovdje su prihodi koji su po naravi
      // kamate, dividende i slicno: 7740 udjeli u fondovima, 7741 dobitci od
      // prodaje dionica, 7743 prihodi od danih zajmova, 7744 prodaja ostale
      // financijske imovine. Dotacije i potpore (7742) idu u red 5.
      const res34 = this.doRdgCalc(6, '7740', '7741', 'oneTotal2', 'totalCredit')
      col5 = res34.prevYear ? col5 + res34.prevYear : col5
      col6 = res34.currYear ? col6 + res34.currYear : col6

      const res34b = this.doRdgCalc(6, '7743', '7744', 'oneTotal2', 'totalCredit')
      col5 = res34b.prevYear ? col5 + res34b.prevYear : col5
      col6 = res34b.currYear ? col6 + res34b.currYear : col6

      this.nI= {...emptyObj}
      this.nI.id = 24
      this.nI.header = false
      this.nI.bold = true
      this.nI.small = false
      this.nI.col1 = '2.'
      this.nI.col2 = '771, 772, 773 i 774'
      this.nI.col3 = 'Kamate, tečajne razlike, dividende i slični prihodi iz odnosa s nepovezanim poduzetnicima i dr.osob.'
      this.nI.col4 = ''
      this.nI.col5 = col5
      this.nI.col6 = col6
      fullRDG.push(this.nI)
      financijskiPrihodiPg += this.nI.col5
      financijskiPrihodiTg += this.nI.col6

      
      const res35 = this.doRdgCalc(2, "775",'', 'oneTotal2', 'totalCredit')
      this.nI= {...emptyObj}
      this.nI.id = 25
      this.nI.header = false
      this.nI.bold = true
      this.nI.small = false
      this.nI.col1 = '3.'
      this.nI.col2 = '775'
      this.nI.col3 = 'Dio prihoda od pridruženih poduzetnika i sudjelujućih interes'
      this.nI.col4 = ''
      this.nI.col5 = res35.prevYear ? res35.prevYear : 0
      this.nI.col6 = res35.currYear ? res35.currYear : 0
      financijskiPrihodiPg += this.nI.col5
      financijskiPrihodiTg += this.nI.col6
      fullRDG.push(this.nI)


      col5 = 0
      col6 = 0
      const res36 = this.doRdgCalc(2, "776",'', 'oneTotal2', 'totalCredit')
      col5 = res36.prevYear ? col5 + res36.prevYear : col5
      col6 = res36.currYear ? col6 + res36.currYear : col6

      const res37 = this.doRdgCalc(2, "777",'', 'oneTotal2', 'totalCredit')
      col5 = res37.prevYear ? col5 + res37.prevYear : col5
      col6 = res37.currYear ? col6 + res37.currYear : col6
      this.nI= {...emptyObj}
      this.nI.id = 26
      this.nI.header = false
      this.nI.bold = true
      this.nI.small = false
      this.nI.col1 = '4.'
      this.nI.col2 = '776 i 777'
      this.nI.col3 = 'Nerealizirani dobitci (prihodi)'
      this.nI.col4 = ''
      this.nI.col5 = col5
      this.nI.col6 = col6
      financijskiPrihodiPg += this.nI.col5
      financijskiPrihodiTg += this.nI.col6
      fullRDG.push(this.nI)

      col5 = 0
      col6 = 0
      // "774 dio" = 7742, dotacije, drzavne potpore i subvencije. To nisu ni
      // kamate ni tecajne razlike ni dividende, pa ne idu u red 2.
      const res38 = this.doRdgCalc(6, '7742', '7742', 'oneTotal2', 'totalCredit')
      col5 = res38.prevYear ? col5 + res38.prevYear : col5
      col6 = res38.currYear ? col6 + res38.currYear : col6

      const res39 = this.doRdgCalc(2, "778",'', 'oneTotal2', 'totalCredit')
      col5 = res39.prevYear ? col5 + res39.prevYear : col5
      col6 = res39.currYear ? col6 + res39.currYear : col6
      this.nI= {...emptyObj}
      this.nI.id = 27
      this.nI.header = false
      this.nI.bold = true
      this.nI.small = false
      this.nI.col1 = '5.'
      this.nI.col2 = '774 dio i 778'
      this.nI.col3 = 'Ostali financijski prihodi'
      this.nI.col4 = ''
      this.nI.col5 = col5
      this.nI.col6 = col6
      financijskiPrihodiPg += this.nI.col5
      financijskiPrihodiTg += this.nI.col6
      fullRDG.push(this.nI)


      fullRDG[financijskiPrihodiIndex-1].col5 = financijskiPrihodiPg
      fullRDG[financijskiPrihodiIndex-1].col6 = financijskiPrihodiTg

      // -------------------------------------------------------------FINANCIJSKI RASHODI
      let financijskiRashodiPg = 0
      let financijskiRashodiTg = 0
      this.nI= {...emptyObj}
      this.nI.id = 28
      this.nI.header = true
      this.nI.bold = true
      this.nI.small = false
      this.nI.col1 = 'IV'
      this.nI.col2 = '47'
      this.nI.col3 = 'FINANCIJSKI RASHODI (1 do 4)'
      this.nI.col4 = ''
      this.nI.col5 = 0
      this.nI.col6 = 0
      
      const financijskiRashodiIndex = fullRDG.push(this.nI)

      col5 = 0
      col6 = 0
      const res41 = this.doRdgCalc(2, "470",'', 'oneTotal1', 'totalDebit')
      col5 = res41.prevYear ? col5 + res41.prevYear : col5
      col6 = res41.currYear ? col6 + res41.currYear : col6

      const res42 = this.doRdgCalc(2, "471",'', 'oneTotal1', 'totalDebit')
      col5 = res42.prevYear ? col5 + res42.prevYear : col5
      col6 = res42.currYear ? col6 + res42.currYear : col6

      const res43 = this.doRdgCalc(2, "472",'', 'oneTotal1', 'totalDebit')
      col5 = res43.prevYear ? col5 + res43.prevYear : col5
      col6 = res43.currYear ? col6 + res43.currYear : col6

      this.nI= {...emptyObj}
      this.nI.id = 29
      this.nI.header = false
      this.nI.bold = true
      this.nI.small = false
      this.nI.col1 = '1.'
      this.nI.col2 = '470, 471 i 472'
      this.nI.col3 = 'Kamate, tečajne razlike i drugi rashodi s povezanim poduzetnicima'
      this.nI.col4 = ''
      this.nI.col5 = col5
      this.nI.col6 = col6
      fullRDG.push(this.nI)
      financijskiRashodiPg += this.nI.col5
      financijskiRashodiTg += this.nI.col6

      col5 = 0
      col6 = 0
      const res44 = this.doRdgCalc(1, '473', '476', 'oneTotal1', 'totalDebit')
      col5 = res44.prevYear ? col5 + res44.prevYear : col5
      col6 = res44.currYear ? col6 + res44.currYear : col6

      // "dio 479" = 4790 do 4792, rashodi valutne klauzule i tecajnih razlika.
      // Ostatak skupine 479 je u redu 4.
      const res45 = this.doRdgCalc(6, '4790', '4792', 'oneTotal1', 'totalDebit')
      col5 = res45.prevYear ? col5 + res45.prevYear : col5
      col6 = res45.currYear ? col6 + res45.currYear : col6

      this.nI= {...emptyObj}
      this.nI.id = 30
      this.nI.header = false
      this.nI.bold = true
      this.nI.small = false
      this.nI.col1 = '2.'
      this.nI.col2 = '473 do 476 i dio 479'
      this.nI.col3 = 'Kamate, tečajne razlike i drugi rashodi s nepovezanim poduzetnicima i dr.osob.'
      this.nI.col4 = ''
      this.nI.col5 = col5
      this.nI.col6 = col6
      fullRDG.push(this.nI)
      financijskiRashodiPg += this.nI.col5
      financijskiRashodiTg += this.nI.col6

      const res46 = this.doRdgCalc(2, "478",'', 'oneTotal1', 'totalDebit')
      this.nI= {...emptyObj}
      this.nI.id = 31
      this.nI.header = false
      this.nI.bold = true
      this.nI.small = false
      this.nI.col1 = '3.'
      this.nI.col2 = '478'
      this.nI.col3 = 'Nerealizirani gubitci (rashodi) financijske imovine'
      this.nI.col4 = ''
      this.nI.col5 = res46.prevYear ? res46.prevYear : 0
      this.nI.col6 = res46.currYear ? res46.currYear : 0
      fullRDG.push(this.nI)
      financijskiRashodiPg += this.nI.col5
      financijskiRashodiTg += this.nI.col6

      // Ovaj red je citao 478 - isto konto kao red 3 - pa se 478 zbrajalo dvaput,
      // a skupina 477 nije ulazila nigdje u RDG. Sada je 477 u cijelosti plus
      // "479 dio" = 4793 do 4799 (burzovne usluge, carina lizinga, pausalne
      // naknade, clanarine za kartice i ostali nespomenuti financijski troskovi).
      col5 = 0
      col6 = 0
      const res47 = this.doRdgCalc(2, '477', '', 'oneTotal1', 'totalDebit')
      if (res47.prevYear) { col5 += res47.prevYear }
      if (res47.currYear) { col6 += res47.currYear }

      const res47b = this.doRdgCalc(6, '4793', '4799', 'oneTotal1', 'totalDebit')
      if (res47b.prevYear) { col5 += res47b.prevYear }
      if (res47b.currYear) { col6 += res47b.currYear }

      this.nI= {...emptyObj}
      this.nI.id = 32
      this.nI.header = false
      this.nI.bold = true
      this.nI.small = false
      this.nI.col1 = '4.'
      this.nI.col2 = '477 i 479 dio'
      this.nI.col3 = 'Ostali financijski rashodi'
      this.nI.col4 = ''
      this.nI.col5 = col5
      this.nI.col6 = col6
      fullRDG.push(this.nI)
      financijskiRashodiPg += this.nI.col5
      financijskiRashodiTg += this.nI.col6

      fullRDG[financijskiRashodiIndex-1].col5 = financijskiRashodiPg
      fullRDG[financijskiRashodiIndex-1].col6 = financijskiRashodiTg

      // -------------------------------------------------------------IZVANREDNI - OSTALI PRIHODI
      col5 = 0
      col6 = 0
      const res48 = this.doRdgCalc(2, "788",'', 'oneTotal1', 'totalCredit')
      col5 = res48.prevYear ? col5 + res48.prevYear : col5
      col6 = res48.currYear ? col6 + res48.currYear : col6

      const res49 = this.doRdgCalc(2, "789",'', 'oneTotal1', 'totalCredit')
      col5 = res49.prevYear ? col5 + res49.prevYear : col5
      col6 = res49.currYear ? col6 + res49.currYear : col6
      console.log('this.pItems', this.pItems)

      let izvanredniPrihodiPg = 0
      let izvanredniPrihodiTg = 0
      this.nI= {...emptyObj}
      this.nI.id = 33
      this.nI.header = true
      this.nI.bold = true
      this.nI.small = false
      this.nI.col1 = 'V'
      this.nI.col2 = '788 i 789'
      this.nI.col3 = 'IZVANREDNI - OSTALI PRIHODI'
      this.nI.col4 = ''
      this.nI.col5 = col5
      this.nI.col6 = col6
      fullRDG.push(this.nI)
      
      // -------------------------------------------------------------IZVANREDNI - OSTALI RASHODI
      let izvanredniRashodiPg = 0
      let izvanredniRashodiTg = 0
      const res50 = this.doRdgCalc(4, "73",'', 'oneTotal1', 'totalDebit') //?
      this.nI= {...emptyObj}
      this.nI.id = 34
      this.nI.header = true
      this.nI.bold = true
      this.nI.small = false
      this.nI.col1 = 'VI'
      this.nI.col2 = '73'
      this.nI.col3 = 'IZVANREDNI - OSTALI RASHODI'
      this.nI.col4 = ''
      this.nI.col5 = res50.prevYear ? res50.prevYear : 0
      this.nI.col6 = res50.currYear ? res50.currYear : 0
      izvanredniRashodiPg = this.nI.col5
      izvanredniRashodiTg = this.nI.col6
      fullRDG.push(this.nI)
      
      // -------------------------------------------------------------UKUPNI PRIHODI (I. + III. + V.)
      this.nI= {...emptyObj}
      this.nI.id = 35
      this.nI.header = true
      this.nI.bold = true
      this.nI.small = false
      this.nI.col1 = 'VII'
      this.nI.col2 = ''
      this.nI.col3 = 'UKUPNI PRIHODI (I. + III. + V.)'
      this.nI.col4 = ''
      this.nI.col5 = poslovniPrihodiPg + financijskiPrihodiPg + izvanredniPrihodiPg
      this.nI.col6 = poslovniPrihodiTg + financijskiPrihodiTg + izvanredniPrihodiTg
      fullRDG.push(this.nI)


      // EBITDA = poslovni prihodi - poslovni rashodi bez amortizacije.
      // Prije se zbrajalo samo redove 3, 4 i 6, pa su ispadali i vrijednosno
      // uskladjivanje (7), rezerviranja (8) i ostali poslovni rashodi (9) - a
      // natrag se smije vratiti samo amortizacija.
      const poslovniRashodiPgE = poslovniRashodiPg - rashod5pg
      const poslovniRashodiTgE = poslovniRashodiTg - rashod5tg
      // -------------------------------------------------------------UKUPNI PRIHODI (I. + III. + V.)
      this.nI= {...emptyObj}
      this.nI.id = 35
      this.nI.header = true
      this.nI.bold = true
      this.nI.small = false
      this.nI.col1 = ''
      this.nI.col2 = ''
      this.nI.col3 = 'EBITDA (posl.prihodi - posl.rashodi(bez amo.))'
      this.nI.col4 = ''
      this.nI.col5 = poslovniPrihodiPg - poslovniRashodiPgE
      this.nI.col6 = poslovniPrihodiTg - poslovniRashodiTgE
      fullRDG.push(this.nI)

      // -------------------------------------------------------------UKUPNI RASHODI (II. + IV. + VI.)
      this.nI= {...emptyObj}
      this.nI.id = 36
      this.nI.header = true
      this.nI.bold = true
      this.nI.small = false
      this.nI.col1 = 'VIII'
      this.nI.col2 = ''
      this.nI.col3 = 'UKUPNI RASHODI (II. + IV. + VI.)'
      this.nI.col4 = ''
      this.nI.col5 = poslovniRashodiPg + financijskiRashodiPg + izvanredniRashodiPg
      this.nI.col6 = poslovniRashodiTg + financijskiRashodiTg + izvanredniRashodiTg
      fullRDG.push(this.nI)

      let dobitPg = 0
      let dobitTk = 0
      let gubitakPg = 0
      let gubitakTk = 0
      // -------------------------------------------------------------DOBITAK PRIJE OPOREZIVANJA (VII. - VIII.)
      if ((poslovniPrihodiPg + financijskiPrihodiPg + izvanredniPrihodiPg) > (poslovniRashodiPg + financijskiRashodiPg + izvanredniRashodiPg)) {
        dobitPg = (poslovniPrihodiPg + financijskiPrihodiPg + izvanredniPrihodiPg) - (poslovniRashodiPg + financijskiRashodiPg + izvanredniRashodiPg)
      }
      if ((poslovniPrihodiTg + financijskiPrihodiTg + izvanredniPrihodiTg) > (poslovniRashodiTg + financijskiRashodiTg + izvanredniRashodiTg)) {
        dobitTk = (poslovniPrihodiTg + financijskiPrihodiTg + izvanredniPrihodiTg) - (poslovniRashodiTg + financijskiRashodiTg + izvanredniRashodiTg)
      }
      this.nI= {...emptyObj}
      this.nI.id = 37
      this.nI.header = true
      this.nI.bold = true
      this.nI.small = false
      this.nI.col1 = 'IX'
      this.nI.col2 = '800'
      this.nI.col3 = 'DOBITAK PRIJE OPOREZIVANJA (VII. - VIII.)'
      this.nI.col4 = ''
      this.nI.col5 = dobitPg
      this.nI.col6 = dobitTk
      fullRDG.push(this.nI)

      // -------------------------------------------------------------GUBITAK PRIJE OPOREZIVANJA (VIII. - VII.)
       if ((poslovniPrihodiPg + financijskiPrihodiPg + izvanredniPrihodiPg) < (poslovniRashodiPg + financijskiRashodiPg + izvanredniRashodiPg)) {
        gubitakPg = (poslovniRashodiPg + financijskiRashodiPg + izvanredniRashodiPg) - (poslovniPrihodiPg + financijskiPrihodiPg + izvanredniPrihodiPg)
      }
      if ((poslovniPrihodiTg + financijskiPrihodiTg + izvanredniPrihodiTg) < (poslovniRashodiTg + financijskiRashodiTg + izvanredniRashodiTg)) {
        gubitakTk = (poslovniPrihodiTg + financijskiPrihodiTg + izvanredniPrihodiTg) - (poslovniRashodiTg + financijskiRashodiTg + izvanredniRashodiTg)
      }
      this.nI= {...emptyObj}
      this.nI.id = 38
      this.nI.header = true
      this.nI.bold = true
      this.nI.small = false
      this.nI.col1 = 'X'
      this.nI.col2 = '801'
      this.nI.col3 = 'GUBITAK PRIJE OPOREZIVANJA (VIII. - VII.)'
      this.nI.col4 = ''
      this.nI.col5 = gubitakPg
      this.nI.col6 = gubitakTk
      fullRDG.push(this.nI)

      // Niza stopa ovisi o UKUPNIM PRIHODIMA, ne o dobiti - Zakon o porezu na
      // dobit prag veze uz prihode poreznog razdoblja. Prije se usporedjivala
      // dobit, pa bi firma s velikim prihodom i malom dobiti dobila 10% umjesto 18%.
      // Prag prethodne godine je 995.421,06 EUR (7,5 mil. kn), tekuce 1.000.000 EUR.
      const ukupniPrihodiPg = poslovniPrihodiPg + financijskiPrihodiPg + izvanredniPrihodiPg
      const ukupniPrihodiTg = poslovniPrihodiTg + financijskiPrihodiTg + izvanredniPrihodiTg
      const porezPg = ukupniPrihodiPg <= 995421.06 ? dobitPg * 0.1 : dobitPg * 0.18
      const porezTg = ukupniPrihodiTg <= 1000000 ? dobitTk * 0.1 : dobitTk * 0.18
      // -------------------------------------------------------------POREZ NA DOBITAK
      this.nI= {...emptyObj}
      this.nI.id = 39
      this.nI.header = true
      this.nI.bold = true
      this.nI.small = false
      this.nI.col1 = 'XI'
      this.nI.col2 = '803'
      this.nI.col3 = 'POREZ NA DOBITAK'
      this.nI.col4 = ''
      this.nI.col5 = porezPg
      this.nI.col6 = porezTg
      fullRDG.push(this.nI)
      

      const dobit = dobitTk - porezTg

      // -------------------------------------------------------------DOBITAK RAZDOBLJA (IX. - XI.)
      this.nI= {...emptyObj}
      this.nI.id = 40
      this.nI.header = true
      this.nI.bold = true
      this.nI.small = false
      this.nI.col1 = 'XII'
      this.nI.col2 = '8040'
      this.nI.col3 = 'DOBITAK RAZDOBLJA (IX. - XI.)'
      this.nI.col4 = ''
      this.nI.col5 = dobitPg - porezPg
      this.nI.col6 = dobitTk - porezTg
      fullRDG.push(this.nI)

      // -------------------------------------------------------------GUBITAK RAZDOBLJA (X. + XI.) ili (XI. - IX.)
      this.nI= {...emptyObj}
      this.nI.id = 40
      this.nI.header = true
      this.nI.bold = true
      this.nI.small = false
      this.nI.col1 = 'XII'
      this.nI.col2 = '8041'
      this.nI.col3 = 'GUBITAK RAZDOBLJA (X. + XI.) ili (XI. - IX.)'
      this.nI.col4 = ''
      this.nI.col5 = gubitakPg
      this.nI.col6 = gubitakTk
      fullRDG.push(this.nI)

      this.fRDG.head = {...this.head}
      this.fRDG.items = fullRDG
      // Prije je isla ravno u pdf. Sada se prvo prikaze, pa se iz prikaza
      // ispisuje ili izvozi.
      this.$refs.izvjestaj.open(this.fRDG, this.currency, 'Račun dobitka i gubitka')
    },
    doRdgCalc(type, from, to, currYearField, prevYearField) {
      const retObj ={}
      retObj.prevYear = 0
      retObj.currYear = 0

      if (type === 1) {
        // go from to to
        const fCond = this.pItems.filter(item => (item.oneCond >= from && item.oneCond <= to) && item.itLine === 'Sin3.ukupno:')
        if (fCond) {
          retObj.currYear = this.calculateSum(fCond, currYearField)
        }
        const fCond2 = this.pOldItems.filter(item => (item.account3 >= from && item.account3 <= to))
        if (fCond2) {
          retObj.prevYear = round(this.calculateSum(fCond2, prevYearField) / this.exRate , 2)
        }
      }
      if (type === 2) {
        // just use from
        const fCond = this.pItems.filter(item => item.oneCond === from && item.itLine === 'Sin3.ukupno:')
        if (fCond) {
          retObj.currYear = this.calculateSum(fCond, currYearField)
        }
        const fCond2 = this.pOldItems.filter(item => item.account3 === from)
        if (fCond2) {
          retObj.prevYear = round(this.calculateSum(fCond2, prevYearField)  / this.exRate,2) 
        }
      }

      if (type === 3) {
        // go from to to
        const fCond = this.pItems.filter(item => (item.oneCond >= from && item.oneCond <= to) && item.itLine === 'Sin.ukupno:')
        if (fCond) {
          retObj.currYear = this.calculateSum(fCond, currYearField)
        }
        const fCond2 = this.pOldItems.filter(item => (item.account2 >= from && item.account2 <= to))
        if (fCond2) {
          retObj.prevYear = round(this.calculateSum(fCond2, prevYearField) / this.exRate ,2)
        }
      }

      if (type === 4) {
        // go from to to
        const fCond = this.pItems.filter(item => item.oneCond === from  && item.itLine === 'Sin.ukupno:')
        if (fCond) {
          retObj.currYear = this.calculateSum(fCond, currYearField)
        }
        const fCond2 = this.pOldItems.filter(item => item.account2 === from )
        if (fCond2) {
          retObj.prevYear = round(this.calculateSum(fCond2, prevYearField) / this.exRate, 2)
        }
      }

      if (type === 5) {
        // just use from
        const fCond = this.pItems.filter(item => item.account4 === from)
        if (fCond) {
          retObj.currYear = this.calculateSum(fCond, currYearField)
        }
        const fCond2 = this.pOldItems.filter(item => item.account4 === from)
        if (fCond2) {
          retObj.prevYear = round(this.calculateSum(fCond2, prevYearField)  / this.exRate,2)
        }
      }

      // type 6 = raspon po analitici, od konta `from` do konta `to`.
      //
      // Obrazac na nekim mjestima pise "dio" - npr. "774 dio i 778" - sto znaci
      // da se skupina dijeli na cetvrtoj znamenki izmedju dva reda. Tipovi 1 do 4
      // rade s trocifrenim i dvocifrenim skupinama pa to ne mogu izraziti.
      //
      // Zbraja se po redcima konta, a ne po zbrojnim redcima grupa, pa idu druga
      // polja: 'oneTotal1' je na zbrojnom retku duguje, a na retku konta 'totDebit'.
      if (type === 6) {
        const poljeTg = currYearField === 'oneTotal2' ? 'totCredit' : 'totDebit'
        const uRasponu = (konto) => {
          if (!konto) { return false }
          const pre = String(konto).substring(0, from.length)
          return pre >= from && pre <= to
        }
        const fCond = this.pItems.filter(item => item.addLine === false && uRasponu(item.account4))
        if (fCond) {
          retObj.currYear = this.calculateSum(fCond, poljeTg)
        }
        const fCond2 = this.pOldItems.filter(item => uRasponu(item.account4))
        if (fCond2) {
          retObj.prevYear = round(this.calculateSum(fCond2, prevYearField) / this.exRate, 2)
        }
      }
    return retObj

    },
    calculateSum(array, property) {
      const total = array.reduce((accumulator, object) => {
        return accumulator + object[property];
      }, 0);
      const total2 = Math.abs(total)
      return total2;
    },

    // Excel prati isti raspored kao pdf: iste kolone, isti redci istim redom.
    downloadFile () {
      exportBalanceXlsx({
        redci: this.vidljivi,
        zbroj: this.zbroj,
        detaljno: this.prikaz === 'detaljno',
        razinaOpis: this.razinaOpis,
        company: this.comp,
        fromDate: this.head.docFromDate,
        toDate: this.head.docToDate,
        fromAccount: this.head.fromAccount,
        toAccount: this.head.toAccount,
        valuta: this.currency,
        brojKonta: this.brojKonta,
        businessYear: this.$store.state.businessYear
      })
    },
    close () {
      this.show = false
    }
  },
  components: {
    MainLedgerAccountCard,
    MainLedgerStatement
  }
}
</script>

<style scoped>
.bilanca {
  font-size: 15px;
  color: #191d1c;
}

/* ---------- zaglavlje ---------- */
.bl-head {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.15fr) auto;
  gap: 20px;
  align-items: start;
  padding: 10px 4px 10px;
  border-bottom: 1px solid #c7cfcb;
}
.bl-firma { line-height: 1.45; color: #4a524f; }
.bl-firma-ime { font-weight: 600; font-size: 15.5px; color: #191d1c; }

.bl-naslov { text-align: center; }
.bl-naslov h2 {
  font-size: 19px;
  font-weight: 600;
  margin: 0 0 3px;
}
.bl-meta { font-size: 14px; color: #4a524f; }
.bl-sep { color: #79827e; padding: 0 4px; }

.bl-akcije { display: flex; gap: 6px; flex-wrap: wrap; justify-content: flex-end; }

/* ---------- kontrole ---------- */
.bl-kontrole {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding: 8px 4px;
  border-bottom: 1px solid #e2e6e3;
  background: #fafbfa;
}
.bl-label {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: .09em;
  color: #79827e;
  font-weight: 600;
}
.bl-label2 { margin-left: 14px; }
.bl-toggle >>> .v-btn { font-size: 13.5px !important; letter-spacing: 0 !important; }
.bl-broj {
  margin-left: auto;
  font-size: 13.5px;
  color: #79827e;
}
.bl-savjet { color: #14534c; }

/* ---------- tablica ---------- */
.bl-scroll { overflow: auto; max-height: 62vh; }
.bl-tbl { border-collapse: collapse; width: 100%; }

.bl-tbl thead th {
  position: sticky;
  background: #fff;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: .07em;
  text-transform: uppercase;
  color: #79827e;
  text-align: right;
  vertical-align: bottom;
  padding: 6px 10px;
  line-height: 1.3;
  white-space: nowrap;
  border-bottom: 1px solid #c7cfcb;
  z-index: 2;
}
.bl-tbl thead th.l { text-align: left; }

/* objedinjeni naslov iznad para kolona */
.bl-tbl thead tr.tier1 th { top: 0; border-bottom: 0; padding-bottom: 2px; }
.bl-tbl thead tr.tier2 th { top: 25px; }
.bl-tbl thead tr.tier1 th.span {
  text-align: center;
  font-size: 12.5px;
  letter-spacing: .12em;
  color: #4a524f;
}
.bl-tbl thead tr.tier1 th.span span {
  display: block;
  padding: 2px 0 3px;
  border-bottom: 2px solid #c7cfcb;
}
.bl-tbl thead tr.tier1 th.span.g-tot { color: #14534c; }
.bl-tbl thead tr.tier1 th.span.g-tot span { border-bottom-color: #14534c; }

/* okomite crte koje drze grupu na okupu kroz cijelu tablicu */
.bl-tbl th.gstart,
.bl-tbl td.gstart { border-left: 1px solid #c7cfcb; }

.bl-tbl tbody td,
.bl-tbl tfoot td {
  padding: 3px 10px;
  border-bottom: 1px solid #e2e6e3;
  vertical-align: baseline;
  white-space: nowrap;
}
.bl-tbl td.num {
  text-align: right;
  font-variant-numeric: tabular-nums;
  font-size: 14px;
}
.bl-tbl td.naziv { white-space: normal; }
.bl-tbl td.konto { font-size: 14px; color: #79827e; }
.bl-tbl td.nula { color: #b6bdb9; }
.bl-tbl td.neg { color: #8c2f1e; }
.dp {
  display: inline-block;
  width: 11px;
  margin-left: 5px;
  font-size: 11.5px;
  font-weight: 600;
  color: #79827e;
  text-align: left;
}

/* hijerarhija uvlakom i crtama, bez podloga i skoka fonta */
.bl-tbl tr.lvl4 td { color: #4a524f; }
.bl-tbl tr.lvl4:hover td { background: #f1f5f3; }
.bl-tbl tr.lvl3 td { font-weight: 500; }
.bl-tbl tr.lvl3 td.naziv { font-style: italic; }
.bl-tbl tr.lvl2 td { font-weight: 600; border-top: 1px solid #c7cfcb; }
.bl-tbl tr.lvl1 td {
  font-weight: 700;
  border-top: 2px solid #14534c;
  padding-top: 7px;
  padding-bottom: 5px;
}
.bl-tbl tr.lvl1 td.naziv { letter-spacing: .04em; text-transform: uppercase; font-size: 14px; }
.bl-tbl tr.lvl1 td.konto { color: #14534c; font-weight: 600; }

.bl-tbl tr.klik { cursor: pointer; }
.bl-tbl tr.klik:focus { outline: 2px solid #14534c; outline-offset: -2px; }
.kartica-hint {
  float: right;
  font-size: 12px;
  letter-spacing: .05em;
  color: #14534c;
  opacity: 0;
  padding-left: 12px;
}
.bl-tbl tr.klik:hover .kartica-hint,
.bl-tbl tr.klik:focus .kartica-hint { opacity: 1; }

.uvlaka { display: inline-block; }
.strelica {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  margin-right: 4px;
  border: 0;
  background: transparent;
  color: #79827e;
  cursor: pointer;
  font-size: 9px;
  line-height: 1;
  padding: 0;
  transition: transform .12s ease;
}
.strelica.otvoren { transform: rotate(90deg); }
.strelica:hover { color: #14534c; }
.strelica.prazna { visibility: hidden; cursor: default; }

.bl-tbl tfoot td {
  position: sticky;
  bottom: 0;
  background: #fafbfa;
  font-weight: 700;
  border-top: 2px solid #c7cfcb;
  border-bottom: 0;
}

/* ---------- kontrolna linija ---------- */
.bl-foot {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  padding: 10px 4px;
  border-top: 1px solid #c7cfcb;
}
.bl-check {
  display: inline-flex;
  align-items: center;
  font-weight: 600;
  font-size: 14px;
  border-radius: 2px;
  padding: 3px 9px;
}
.bl-check.ok { color: #1f6b43; background: #e7f1ea; border: 1px solid #b4d6c3; }
.bl-check.lose { color: #8c2f1e; background: #fbeeea; border: 1px solid #e0b2a6; }
.bl-sums {
  margin-left: auto;
  display: flex;
  gap: 18px;
  font-size: 14px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}
.bl-sums em { font-style: normal; font-weight: 400; color: #79827e; padding-right: 5px; }

@media (max-width: 820px) {
  .bl-head { grid-template-columns: 1fr; }
  .bl-naslov { text-align: left; }
  .bl-akcije { justify-content: flex-start; }
}
</style>
